/**
 * Optional sqlite-vec ANN acceleration for {@link BlobVectorStore}.
 *
 * `sqlite-vec` is an optionalDependency. When it is installed and loads into the
 * connection, vector search drops from O(N) brute-force cosine to a sub-linear
 * vec0 KNN scan. When it is absent (Windows/CI/offline, or the load fails),
 * {@link Vec0Index.tryCreate} returns `null` and the caller keeps using
 * brute-force — zero behaviour change, identical results.
 *
 * The vec0 table is a derived ACCELERATOR over the canonical `symbol_embeddings`
 * BLOB table, never a replacement: the BLOB table still backs drift checks,
 * `repair_index` drop-vec, and the brute-force fallback.
 *
 * ponytail: vec0 mirrors the BLOB table, so vector storage roughly doubles while
 * the extension is active — the standard price of an ANN index. Uninstall
 * sqlite-vec to drop back to brute-force and reclaim the space.
 *
 * sqlite-vec binding quirks baked in here:
 *  - the integer primary key must be bound as a BigInt (a plain JS number is
 *    rejected with "Only integers are allowed for primary key values");
 *  - vectors are passed as raw little-endian float32 BLOBs wrapped in `vec_f32(?)`;
 *  - the column is declared `distance_metric=cosine`, so the `distance` it
 *    returns is `1 - cosine_similarity` — we invert it back to a similarity score
 *    to honour the {@link VectorStore} contract (higher = better).
 */
import type Database from 'better-sqlite3';
import { createRequire } from 'node:module';

const require = createRequire(import.meta.url);

/**
 * SQLite has no parameter-binding mechanism for identifiers (table/column
 * names can't be bound params), so any table name interpolated into SQL
 * text — as every method below does with `this.table` — must be validated
 * against a strict allowlist first (CWE-89 defense-in-depth). This mirrors
 * a standard `[a-zA-Z_][a-zA-Z0-9_]*` identifier: starts with a letter or
 * underscore, followed by letters/digits/underscores only. No quotes,
 * whitespace, semicolons, or SQL keywords can ever match.
 */
const SAFE_SQLITE_IDENTIFIER_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export function isSafeSqliteIdentifier(name: string): boolean {
  return typeof name === 'string' && SAFE_SQLITE_IDENTIFIER_RE.test(name);
}

function assertSafeSqliteIdentifier(name: string): void {
  if (!isSafeSqliteIdentifier(name)) {
    throw new Error(`Vec0Index: unsafe table identifier: ${JSON.stringify(name)}`);
  }
}

/**
 * Name of the sqlite-vec accelerator table over `symbol_embeddings`.
 * Shared with the symbol-delete path (SymbolRepository removes vec rows for
 * deleted symbols without loading the extension — vec0 has no FK to follow).
 */
export const VEC_TABLE_NAME = 'vec_symbol_embeddings';

export class Vec0Index {
  private readonly table = VEC_TABLE_NAME;
  private dim: number | null = null;
  private ready = false;

  private constructor(private readonly db: Database.Database) {
    // Defense-in-depth: `table` is a hardcoded literal today and never
    // derived from external/config input, but every method below builds SQL
    // by interpolating it directly. Validating once at construction means
    // any future change that makes `table` configurable fails fast here
    // instead of silently building unsafe SQL.
    assertSafeSqliteIdentifier(this.table);
  }

  /** Load sqlite-vec into the connection. Returns null when unavailable. */
  static tryCreate(db: Database.Database): Vec0Index | null {
    // Already loaded on this connection (e.g. a second store over the same db)?
    try {
      db.prepare('SELECT vec_version()').get();
      return new Vec0Index(db);
    } catch {
      /* not loaded yet — try to load below */
    }
    try {
      const sqliteVec = require('sqlite-vec') as { load: (db: Database.Database) => void };
      sqliteVec.load(db);
      db.prepare('SELECT vec_version()').get();
      return new Vec0Index(db);
    } catch {
      return null;
    }
  }

  /** Create the vec0 virtual table for a given dimensionality (idempotent per dim). */
  private ensure(dim: number): boolean {
    if (this.ready) return this.dim === dim;
    try {
      this.db.exec(
        `CREATE VIRTUAL TABLE IF NOT EXISTS ${this.table} USING vec0(` +
          `symbol_id integer primary key, embedding float[${dim}] distance_metric=cosine)`,
      );
      this.dim = dim;
      this.ready = true;
      return true;
    } catch {
      return false;
    }
  }

  insert(id: number, vectorBuf: Buffer, dim: number): void {
    if (!this.ensure(dim)) return;
    try {
      // vec0 does NOT honour INSERT OR REPLACE (it throws UNIQUE on the pk), so
      // emulate upsert with delete-then-insert — otherwise a replaced embedding
      // would silently keep its stale vector.
      const bid = BigInt(id);
      this.db.prepare(`DELETE FROM ${this.table} WHERE symbol_id = ?`).run(bid);
      this.db
        .prepare(`INSERT INTO ${this.table}(symbol_id, embedding) VALUES (?, vec_f32(?))`)
        .run(bid, vectorBuf);
    } catch {
      /* the BLOB table is canonical; a vec0 hiccup must never break indexing */
    }
  }

  delete(id: number): void {
    if (!this.ready) return;
    try {
      this.db.prepare(`DELETE FROM ${this.table} WHERE symbol_id = ?`).run(BigInt(id));
    } catch {
      /* ignore — stale accelerator row is harmless vs the canonical BLOB table */
    }
  }

  /**
   * Delete many accelerator rows at once (GH#1447: the symbol-delete path).
   * No-ops when the table was never created. Never throws — a vec0 hiccup
   * must never break indexing; residual staleness self-heals on the next
   * BlobVectorStore open via syncVecIndex.
   */
  deleteMany(ids: number[]): void {
    if (ids.length === 0) return;
    if (!this.tableExists()) return;
    try {
      // TRA-1005: chunk — one `IN (?,...)` over the full array tops
      // SQLITE_MAX_VARIABLE_NUMBER past ~32k symbols.
      const CHUNK = 900;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const placeholders = chunk.map(() => '?').join(',');
        // vec0 integer pks must be bound as BigInt (see insert()).
        this.db
          .prepare(`DELETE FROM ${this.table} WHERE symbol_id IN (${placeholders})`)
          .run(...chunk.map((id) => BigInt(id)));
      }
    } catch {
      /* see delete() */
    }
  }

  /** True when the vec0 table exists in this database. */
  private tableExists(): boolean {
    try {
      const row = this.db
        .prepare(`SELECT name FROM sqlite_master WHERE type = 'table' AND name = ? LIMIT 1`)
        .get(this.table);
      return row !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Accelerator rows whose symbol no longer exists (GH#1447). Counts against
   * `symbols` (not the BLOB table) so FK-OFF windows that orphaned BLOB rows
   * too are still detected. Returns 0 when the table is absent/unreadable.
   */
  countOrphans(): number {
    if (!this.tableExists()) return 0;
    try {
      const row = this.db
        .prepare(
          `SELECT COUNT(*) AS c FROM ${this.table} WHERE symbol_id NOT IN (SELECT id FROM symbols)`,
        )
        .get() as { c: number } | undefined;
      return row?.c ?? 0;
    } catch {
      return 0;
    }
  }

  /** Cheap existence probe for the sync fast path — LIMIT 1, no full scan. */
  hasOrphans(): boolean {
    if (!this.tableExists()) return false;
    try {
      const row = this.db
        .prepare(
          `SELECT 1 AS one FROM ${this.table} WHERE symbol_id NOT IN (SELECT id FROM symbols) LIMIT 1`,
        )
        .get();
      return row !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Canonical BLOB rows with no accelerator row. Returns -1 when unknown (no
   * vec table yet, or the vec0 module refuses the membership scan) — the
   * caller then falls back to a full backfill.
   */
  countMissing(): number {
    if (!this.tableExists()) return -1;
    try {
      const row = this.db
        .prepare(
          `SELECT COUNT(*) AS c FROM symbol_embeddings WHERE symbol_id NOT IN (SELECT symbol_id FROM ${this.table})`,
        )
        .get() as { c: number } | undefined;
      return row?.c ?? -1;
    } catch {
      return -1;
    }
  }

  /**
   * Delete every accelerator row whose symbol no longer exists. Returns the
   * number of rows removed (0 when absent/unreadable).
   */
  pruneOrphans(): number {
    if (!this.tableExists()) return 0;
    try {
      const info = this.db
        .prepare(`DELETE FROM ${this.table} WHERE symbol_id NOT IN (SELECT id FROM symbols)`)
        .run();
      return info.changes;
    } catch {
      return 0;
    }
  }

  clear(): void {
    // Defensive re-check immediately before the DDL statement (belt-and-
    // suspenders on top of the constructor-time assertion), in case `table`
    // is ever mutated post-construction. Intentionally outside the try/catch
    // below — an unsafe identifier must fail loudly, not be swallowed by the
    // "vec0 hiccup is non-fatal" error handling that follows.
    assertSafeSqliteIdentifier(this.table);
    try {
      this.db.exec(`DROP TABLE IF EXISTS ${this.table}`);
    } catch {
      /* ignore */
    }
    this.ready = false;
    this.dim = null;
  }

  count(): number {
    if (!this.ready) return 0;
    try {
      const row = this.db.prepare(`SELECT COUNT(*) AS c FROM ${this.table}`).get() as { c: number };
      return row.c;
    } catch {
      return 0;
    }
  }

  /**
   * KNN search. Returns `null` to signal "not usable — fall back to brute-force"
   * (dim mismatch, or any vec0 error). An empty array is a real "no matches".
   */
  search(queryBuf: Buffer, limit: number, dim: number): { id: number; score: number }[] | null {
    if (!this.ensure(dim)) return null;
    try {
      const rows = this.db
        .prepare(
          `SELECT symbol_id AS id, distance FROM ${this.table} ` +
            `WHERE embedding MATCH vec_f32(?) ORDER BY distance LIMIT ?`,
        )
        .all(queryBuf, limit) as { id: number | bigint; distance: number }[];
      return rows.map((r) => ({ id: Number(r.id), score: 1 - r.distance }));
    } catch {
      return null;
    }
  }

  /**
   * Backfill a materialized chunk into the accelerator. The caller must pass a
   * fully-read array (not a live better-sqlite3 iterator) — writing while a read
   * iterator is open on the same connection throws.
   */
  backfill(rows: Array<{ symbol_id: number | bigint; embedding: Buffer }>, dim: number): void {
    if (!this.ensure(dim)) return;
    try {
      // delete-then-insert: vec0 rejects INSERT OR REPLACE (see insert()), and this
      // keeps backfill idempotent if it re-runs over a partially-populated index.
      const del = this.db.prepare(`DELETE FROM ${this.table} WHERE symbol_id = ?`);
      const ins = this.db.prepare(
        `INSERT INTO ${this.table}(symbol_id, embedding) VALUES (?, vec_f32(?))`,
      );
      const tx = this.db.transaction(() => {
        for (const r of rows) {
          const bid = BigInt(r.symbol_id);
          del.run(bid);
          ins.run(bid, r.embedding);
        }
      });
      tx();
    } catch {
      /* leave vec0 partially filled; search() returns null and brute-force covers it */
    }
  }
}
